import {
  SERVICE_BILLING_CADENCE_OPTIONS,
  SERVICE_WEEKDAY_OPTIONS,
} from './serviceOptions';

export const DEFAULT_BILLING_CADENCE = 'per_trip';

export const resolveContractBillingCadence = (contract = null) => {
  const explicit = String(contract?.billingCadence || '').trim().toLowerCase();
  if (['per_trip', 'weekly', 'monthly'].includes(explicit)) return explicit;

  const commercialModel = String(contract?.commercialModel || '').trim().toLowerCase();
  if (commercialModel === 'trip_rate') return 'per_trip';
  if (commercialModel === 'monthly_fixed') return 'monthly';
  if (commercialModel === 'monthly_variable') return 'monthly';
  return DEFAULT_BILLING_CADENCE;
};

export const resolveBillingCadenceLabel = (value = DEFAULT_BILLING_CADENCE) =>
  SERVICE_BILLING_CADENCE_OPTIONS.find((option) => option.value === String(value || '').trim())?.label ||
  'Por viaje';

export const resolveWeekdayLabel = (value = '') =>
  SERVICE_WEEKDAY_OPTIONS.find((option) => option.value === String(value || '').trim())?.label || 'Sin día';

export const resolveServiceBillingCadence = ({
  linkedContract = null,
  frequency = 'once',
  outsideContract = false,
  explicitCadence = '',
} = {}) => {
  const normalizedExplicit = String(explicitCadence || '').trim().toLowerCase();
  if (['per_trip', 'weekly', 'monthly'].includes(normalizedExplicit)) return normalizedExplicit;

  if (outsideContract) return 'per_trip';
  if (String(frequency || '').trim().toLowerCase() === 'once') return 'per_trip';
  return resolveContractBillingCadence(linkedContract);
};

export const resolveOutsideContractFlag = ({
  linkedContract = null,
  frequency = 'once',
} = {}) => {
  if (!linkedContract) return false;
  if (String(frequency || '').trim().toLowerCase() !== 'once') return false;
  return resolveContractBillingCadence(linkedContract) !== 'per_trip';
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

export const buildClosurePeriod = (referenceDate = new Date(), cadence = DEFAULT_BILLING_CADENCE) => {
  const safeDate = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(safeDate.getTime())) {
    const now = new Date();
    return buildClosurePeriod(now, cadence);
  }

  if (cadence === 'weekly') {
    const day = safeDate.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const start = startOfDay(new Date(safeDate.getFullYear(), safeDate.getMonth(), safeDate.getDate() + diff));
    const end = endOfDay(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6));
    return {
      cadence,
      periodStart: start,
      periodEnd: end,
      label: `Semana ${start.toLocaleDateString('es-AR')} al ${end.toLocaleDateString('es-AR')}`,
    };
  }

  if (cadence === 'monthly') {
    const start = new Date(safeDate.getFullYear(), safeDate.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(safeDate.getFullYear(), safeDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return {
      cadence,
      periodStart: start,
      periodEnd: end,
      label: safeDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }),
    };
  }

  return {
    cadence: 'per_trip',
    periodStart: startOfDay(safeDate),
    periodEnd: endOfDay(safeDate),
    label: 'Por viaje',
  };
};

export const buildServiceClosureKey = ({
  clientId = '',
  contractId = '',
  cadence = DEFAULT_BILLING_CADENCE,
  periodStart,
}) => {
  const safeClientId = String(clientId || 'client').trim() || 'client';
  const safeContractId = String(contractId || 'no-contract').trim() || 'no-contract';
  const safeCadence = String(cadence || DEFAULT_BILLING_CADENCE).trim() || DEFAULT_BILLING_CADENCE;
  const safePeriodStart =
    periodStart instanceof Date && !Number.isNaN(periodStart.getTime())
      ? periodStart.toISOString().slice(0, 10)
      : 'no-period';

  return [safeClientId, safeContractId, safeCadence, safePeriodStart]
    .join('__')
    .replace(/[^\w-]+/g, '_');
};

export const isServiceRevenueRecognized = (service = {}) => {
  if (service?.recognizedInBalance === true) return true;
  if (service?.recognizedAt) return true;
  if (service?.revenueRecognizedAt) return true;

  const cadence = String(service?.billingCadenceSnapshot || '').trim().toLowerCase();
  if (!cadence && String(service?.financialRecognitionMode || '').trim().toLowerCase() === 'per_trip') {
    return Boolean(service?.financialClosedAt || service?.completedAt);
  }

  return cadence === 'per_trip' && Boolean(service?.financialClosedAt || service?.completedAt);
};

export const resolveServiceRecognitionBadge = (service = {}) => {
  const cadence = String(service?.billingCadenceSnapshot || '').trim().toLowerCase() || 'per_trip';
  const recognized = isServiceRevenueRecognized(service);

  if (cadence === 'weekly') {
    return recognized
      ? { label: 'Semanal · Pagado', tone: 'border-emerald-200 bg-emerald-50 text-emerald-700' }
      : { label: 'Semanal · Pendiente', tone: 'border-amber-200 bg-amber-50 text-amber-700' };
  }

  if (cadence === 'monthly') {
    return recognized
      ? { label: 'Mensual · Pagado', tone: 'border-emerald-200 bg-emerald-50 text-emerald-700' }
      : { label: 'Mensual · Pendiente', tone: 'border-amber-200 bg-amber-50 text-amber-700' };
  }

  return service?.outsideContract
    ? { label: 'Viaje único · Fuera del contrato', tone: 'border-cyan-200 bg-cyan-50 text-cyan-700' }
    : { label: 'Por viaje', tone: 'border-cyan-200 bg-cyan-50 text-cyan-700' };
};

export const resolveClosureStatusLabel = (status = 'open') => {
  const normalized = String(status || 'open').trim().toLowerCase();
  if (normalized === 'paid') return 'Pagado';
  if (normalized === 'ready_to_bill') return 'Listo para cobrar';
  return 'Abierto';
};
